// @ts-check
import React, {useState} from "react"
import {Link} from "gatsby"
import {StaticImage} from "gatsby-plugin-image"

import {useBreakpoint} from "../hooks/useBreakpoint"

import Button from "./button"
import Collapse from "./collapse"
import {ForwardIcon} from "./icons"
import "../stories/header.css"
import closeIcon from "../images/ic_close.svg"
import hamburgerIcon from "../images/ic_menu.svg"
import closeIconWhite from "../images/ic_close_white.svg"
import hamburgerIconWhite from "../images/ic_menu_white.svg"
import {navigate} from "gatsby"

const fanXpUrl = "fan-experience"

/**
 * @type {React.FC<{
 *  navbar: import('../data/global').GlobalNavBar,
 *  preview?: boolean
 *  pathName?: string
 *  slug?: string
 * }>}
 */
const Header = ({navbar, preview = false, pathName, slug}) => {
  const {links, logo, whiteLogo} = navbar
  const {isDesktop} = useBreakpoint()

  return (
    <header
      className={`layout-header ${
        slug === fanXpUrl || pathName?.includes(fanXpUrl)
          ? "header-section"
          : "white"
      }`}>
      <Link
        to={preview ? "/preview" : "/"}
        className={isDesktop ? "basis-1/6" : undefined}>
        {pathName?.includes(fanXpUrl) ? (
          whiteLogo ? (
            <img src={whiteLogo} />
          ) : null
        ) : logo?.url ? (
          <img src={logo.url} alt={logo.alternativeText} />
        ) : null}
      </Link>
      {isDesktop ? (
        <DesktopNavBarMenu links={links} pathName={pathName} slug={slug} />
      ) : (
        <MobileNavBarMenu links={links} pathName={pathName} slug={slug} />
      )}
    </header>
  )
}

/**
 * @template T
 * @param {() => void} callback
 * @returns {(event: React.KeyboardEvent<T>) => void}
 */
function whenPressEnter(callback) {
  // TODO: maybe need optimization
  return event => {
    if (event.code === "Enter") {
      callback()
    }
  }
}

/**
 * @type {React.FC<{
 *   links: import('../data/global').GlobalNavBar["links"],
 * }>}
 **/
const MobileNavBarMenu = ({links = [], pathName = "", slug = ""}) => {
  const [openMenu, setOpenMenu] = useState(false)
  return (
    <>
      <button onClick={() => setOpenMenu(prev => !prev)}>
        {openMenu ? (
          slug === fanXpUrl || pathName?.includes(fanXpUrl) ? (
            <img src={closeIconWhite} alt="close" />
          ) : (
            <img src={closeIcon} alt="close" />
          )
        ) : slug === fanXpUrl || pathName?.includes(fanXpUrl) ? (
          <img src={hamburgerIconWhite} alt="menu" />
        ) : (
          <img src={hamburgerIcon} alt="menu" />
        )}
      </button>
      <nav
        className={`header-navbar bg-primary text-white ${
          openMenu ? "fixed" : "hidden"
        }`}>
        <div>
          <ul>
            {links
              .filter(d => !d.hidden)
              .map(({title, subLinks = []}, index) => (
                <ExpandableLinkListItem
                  handleMenu={() => setOpenMenu(prev => !prev)}
                  key={String(index)}
                  title={title}
                  links={subLinks}
                  itemClass="py-[15px] border-b border-b-[#6F5EA9] border-solid"
                  linkClass="py-[15px]"
                />
              ))}
          </ul>
          <li className="flex flex-row justify-between items-center py-[15px] border-b border-b-[#6F5EA9] border-solid">
            <div>
              <div className="flex flex-col">
                <Link
                  to={"/pricing"}
                  onClick={() => setOpenMenu(false)}>
                    Pricing
                </Link>
              </div>
            </div> 
          </li>
          <div className="flex flex-col px-[4.5rem] pt-[4rem]">
            <Button
              onClick={() => setOpenMenu(prev => !prev)}
              type="secondary"
              className=" mb-[1.875rem]"
              path="/contact-us">
              Contact Us
            </Button>
            <Link to={links[7].subLinks[0].url} target="_blank">
              <Button
                onClick={() => setOpenMenu(prev => !prev)}
                outline
                className="!text-white !border-white">
                Login
              </Button>
            </Link>
          </div>
        </div>
      </nav>
    </>
  )
}

// TODO: move to real place
/**
 * @type {React.FC<{
 *   links: import('../data/global').HeaderSubLinks,
 *   containerClass?: string,
 *   itemClass?: string,
 *   linkClass?: string,
 *   title: string,
 * }>}
 * */
const ExpandableLinkListItem = ({
  links = [],
  containerClass = "",
  itemClass = "",
  linkClass = "",
  title,
  handleMenu,
}) => {
  const [isExpand, setIsExpand] = useState(false)
  const toggleExpand = () => setIsExpand(prev => !prev)

  return (
    <li className={containerClass}>
      <div
        tabIndex={0}
        role="button"
        className={"flex flex-row justify-between items-center " + itemClass}
        onClick={toggleExpand}
        onKeyDown={whenPressEnter(toggleExpand)}>
        <span>{title}</span>
        <ForwardIcon
          className={`text-white ${isExpand ? "rotate-180" : "-rotate-90"}`}
        />
      </div>
      <Collapse in={!isExpand}>
        <div className="flex flex-col">
          {links
            .filter(d => !d.hidden)
            .map(({url, title: where}, index) => (
              <Link
                key={String(index)}
                to={url || "#"}
                className={linkClass}
                onClick={() => handleMenu()}>
                {where}
              </Link>
            ))}
        </div>
      </Collapse>
    </li>
  )
}

/**
 * @type {React.FC<{
 *  links: import('../data/global').GlobalNavBar["links"],
 *  slug: string,
 *  pathName: string,
 * }>}
 */
const DesktopNavBarMenu = ({links = [], pathName, slug}) => {
   // Enable dissable approve button in staging environment start
   let hostNames = ["localhost","staging.dropthought.com"]
   let currentHostName = window.location.hostname
   const value = hostNames.includes(currentHostName)?true : false
  //  const loginURL = window.location.href.includes('localhost') ? 'https://stage.dropthought.com/' : 'https://app.dropthought.com/';
  // const loginURL = 'https://app.dropthought.com/';
  const loginURL = process.env.DT_API_URL.includes('stage') ? 'https://stage.dropthought.com/' : 'https://app.dropthought.com/'
   const approveButtonClicked = () => {
    console.log('approve button clicked');
    // call the get api to approve jenkins job
    fetch('https://dt-web-strapi.dropthought.com/dtwebsite/promote', {
        method: 'GET',
        headers: {
          'api-token': '2pU9mr!eWY/VWsuS85Gf2K2csKjNCbX9GNVFT54Rwgbtrb-dEUi1QBcgUUy33I7Mxfj2QFLk6jPZUZQzOTZVw0IZRCWj9kQ0oJXJ1/-WiZYN?F9VI0=p4UOE9!eVXCuB',
        }
      })
      .then(response => response.json())
      .then(data => {
        // Handle the response data
        console.log('success job done',data);
        window.alert('Success! trigger the build to production done.');
      })
      .catch(error => {
        // Handle any errors
        console.error('Fail job not done',error);
        window.alert('Failure! trigger the build to production not done.');
      });
  }
   // Enable dissable approve button in staging environment end
  return (
    <>
      <div className="justify-self-center max-w-[620px] w-full ">
        <ul className="flex flex-row justify-between ">
          {links
            .filter(d => !d.hidden)
            .map(({title, subLinks}, index) => (
              <DesktopNavBarListItem
                key={String(index)}
                title={title}
                links={subLinks}
                pathName={pathName}
                slug={slug}
              />
            ))
            }
            <DesktopNavBarListItem
                title='Pricing'
                pathName={pathName}
            />
          {/* <li className="relative group rounded-lg hover:bg-gray-2">
            <div>
              <button className="leading-normal p-2" onClick={handleButtonClickPricing}>Pricing</button>
            </div> 
          </li> */}
             
        </ul>
      </div>
      <div className="flex flex-row items-center">
        <Link to={loginURL} target="_blank">
          <Button
            type="primary"
            className={`px-2 py-2 ${
              slug === fanXpUrl || pathName?.includes(fanXpUrl)
                ? "login-btn border-none"
                : "btn--outline btn-primary !border-white"
            }`}>
            Login
          </Button>
        </Link>
        <Button
          type="primary"
          className={`px-7 ml-4 leading-5 ${
            slug === fanXpUrl || pathName?.includes(fanXpUrl)
              ? "demo-btn btn--outline border-none"
              : "btn-primary"
          }`}
          path="/contact-us">
          Contact Us
        </Button>
      {value == true ? (<Button
          type="primary"
          className={`px-7 ml-4 leading-5 ${
            slug === fanXpUrl || pathName?.includes(fanXpUrl)
              ? "demo-btn btn--outline border-none"
              : "btn-primary"
          }`}
          // Edit the path to the page you want to redirect to
          path="" onClick={approveButtonClicked}>
          Trigger the build to production
        </Button>) : ''}
      </div>
    </>
  )
}

const handleButtonClickPricing = () => {
  navigate("/pricing")
}

/** @type {React.FC<{links: import('../data/global').HeaderSubLinks, title:string}>} */
const DesktopNavBarListItem = ({links = [], title, pathName, slug}) => {
  console.log("links", links, "title", title, "pathName", pathName, "slug", slug)
  if (title === "Team") return null
  return (
    <li
      className={`relative group rounded-lg ${
        slug === fanXpUrl || pathName?.includes(fanXpUrl)
          ? "c-dark"
          : "hover:bg-gray-2"
      } `}>
      <Link
        to={title === "Team" ? "/team" : title === "Pricing" ? "/pricing" : ""}
        className={`leading-normal p-2 ${
          slug === fanXpUrl || pathName?.includes(fanXpUrl)
            ? "nav-btn-color"
            : ""
        }`}>
        <button
          className={`leading-normal p-2 ${
            slug === fanXpUrl || pathName?.includes(fanXpUrl)
              ? "nav-btn-color"
              : ""
          }`}>
          {/* <a href="javascript:void(0)"> */}
          {title}
          {/* </a> */}
        </button>
      </Link>
      {/* <button className={`leading-normal p-2 ${((slug === fanXpUrl) || pathName?.includes(fanXpUrl)) ? 'nav-btn-color': '' }`}>{title}</button> */}
      {title !== "Team" ? (
        <div className="absolute hidden group-hover:flex flex-col top-full min-w-max py-2 bg-white shadow rounded-sm">
          {links
            .filter(d => !d.hidden)
            .map(({url, title: where}, index) => (
              <Link
                key={String(index)}
                to={url || "#"}
                className="px-4 py-2 hover:bg-gray-2">
                {where}
              </Link>
            ))}
        </div>
      ) : null}
    </li>
  )
}

export default Header
